import React, { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { TextBlockContent, CommunityPageSectionRow } from '@/types/cms';
import { Save, X, Info } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface TextBlockEditorProps {
  section: CommunityPageSectionRow;
  onSave: (updates: Partial<CommunityPageSectionRow>) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const TextBlockEditor: React.FC<TextBlockEditorProps> = ({
  section,
  onSave,
  onCancel,
  isLoading = false
}) => {
  const content = section.content as TextBlockContent;
  
  const [formData, setFormData] = useState({
    title: section.title || '',
    content: content.content || '',
    alignment: content.alignment || 'left',
    max_width: content.max_width || 800,
    show_card: content.show_card !== undefined ? content.show_card : true,
    card_style: content.card_style || 'default',
    container_padding: content.container_padding || 'default'
  });

  useEffect(() => {
    setFormData({
      title: section.title || '',
      content: content.content || '',
      alignment: content.alignment || 'left',
      max_width: content.max_width || 800,
      show_card: content.show_card !== undefined ? content.show_card : true,
      card_style: content.card_style || 'default',
      container_padding: content.container_padding || 'default'
    });
  }, [section, content]);

  const handleInputChange = (field: string, value: string | number | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    const { title, ...contentFields } = formData;
    onSave({
      title: title,
      content: {
        ...content,
        ...contentFields
      }
    });
  };

  return (
    <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
      <div className="flex items-center justify-between mb-4">
        <h4 className="font-medium">Edit Text Block Section</h4>
        <div className="flex gap-2">
          <Button size="sm" onClick={handleSave} disabled={isLoading}>
            <Save className="h-4 w-4 mr-2" />
            Save
          </Button>
          <Button variant="outline" size="sm" onClick={onCancel}>
            <X className="h-4 w-4 mr-2" />
            Cancel
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4">
        {/* Title */}
        <div className="space-y-2">
          <Label htmlFor="text-title">Section Title (Optional)</Label>
          <Input
            id="text-title"
            value={formData.title}
            onChange={(e) => handleInputChange('title', e.target.value)}
            placeholder="Enter section title"
          />
        </div>

        {/* Content */}
        <div className="space-y-2">
          <Label htmlFor="text-content">Content</Label>
          <Textarea
            id="text-content"
            value={formData.content}
            onChange={(e) => handleInputChange('content', e.target.value)}
            placeholder="Enter your content here..."
            rows={8}
            className="font-mono text-sm"
          />
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription className="text-xs">
              You can use basic formatting: **bold**, *italic*, `code`, and line breaks.
            </AlertDescription>
          </Alert>
        </div>

        {/* Layout Settings */}
        <div className="space-y-4 pt-4 border-t">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="text-alignment">Text Alignment</Label>
              <Select
                value={formData.alignment}
                onValueChange={(value) => handleInputChange('alignment', value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="left">Left</SelectItem>
                  <SelectItem value="center">Center</SelectItem>
                  <SelectItem value="right">Right</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="text-max-width">Max Width</Label>
              <Select
                value={formData.max_width.toString()}
                onValueChange={(value) => handleInputChange('max_width', parseInt(value))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="600">Narrow (600px)</SelectItem>
                  <SelectItem value="800">Medium (800px)</SelectItem>
                  <SelectItem value="1000">Wide (1000px)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Styling Settings */}
          <div className="space-y-4 pt-4 border-t">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="show-card">Card Layout</Label>
                <p className="text-xs text-muted-foreground">
                  Enable card styling with borders and background
                </p>
              </div>
              <Switch
                id="show-card"
                checked={formData.show_card}
                onCheckedChange={(checked) => handleInputChange('show_card', checked)}
              />
            </div>

            {formData.show_card && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="card-style">Card Style</Label>
                  <Select
                    value={formData.card_style}
                    onValueChange={(value) => handleInputChange('card_style', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="default">Default (gradient)</SelectItem>
                      <SelectItem value="minimal">Minimal (plain)</SelectItem>
                      <SelectItem value="bordered">Bordered</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="container-padding">Spacing</Label>
                  <Select
                    value={formData.container_padding}
                    onValueChange={(value) => handleInputChange('container_padding', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="compact">Compact</SelectItem>
                      <SelectItem value="default">Default</SelectItem>
                      <SelectItem value="spacious">Spacious</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {!formData.show_card && (
              <div className="space-y-2">
                <Label htmlFor="container-padding-flat">Spacing</Label>
                <Select
                  value={formData.container_padding}
                  onValueChange={(value) => handleInputChange('container_padding', value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="compact">Compact</SelectItem>
                    <SelectItem value="default">Default</SelectItem>
                    <SelectItem value="spacious">Spacious</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </div>

        {/* Preview */}
        <div className="space-y-2 pt-4 border-t">
          <Label>Preview</Label>
          <div className={`p-4 rounded-lg ${
            formData.show_card 
              ? formData.card_style === 'default' 
                ? 'bg-gradient-to-br from-white to-gray-50/30 border shadow-sm'
                : formData.card_style === 'bordered'
                ? 'bg-white border-2 border-gray-200'
                : 'bg-white'
              : 'bg-gray-50'
          }`}>
            <div 
              className={`text-${formData.alignment}`}
              style={{ maxWidth: `${formData.max_width}px` }}
            >
              {formData.title && (
                <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                  {formData.show_card && <span className="w-4 h-4 bg-primary rounded-sm"></span>}
                  {formData.title}
                </h3>
              )}
              <div className="prose prose-sm max-w-none">
                {formData.content ? (
                  formData.content.split('\n\n').map((paragraph, index) => (
                    <p key={index} className="mb-4 last:mb-0">
                      {paragraph.split('\n').map((line, lineIndex) => (
                        <React.Fragment key={lineIndex}>
                          {lineIndex > 0 && <br />}
                          {line}
                        </React.Fragment>
                      ))}
                    </p>
                  ))
                ) : (
                  <p className="text-muted-foreground italic">No content entered yet</p>
                )}
              </div>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            Layout: {formData.show_card ? 'Card' : 'Flat'} • 
            Spacing: {formData.container_padding} • 
            Alignment: {formData.alignment}
          </p>
        </div>
      </div>
    </div>
  );
};

export default TextBlockEditor;